import { t, localePrefix } from "@/helper/helper";
import useFavorite from "@/hooks/common/useFavorite";
import { IBrandsGeneric, IProductDataGeneric } from "global-types/global.types";
import Image from "next/image";
import Link from "next/link";
import { parseCookies } from "nookies";
import React, { UIEvent } from "react";
import styled from "styled-components";

interface Props {
  data: IProductDataGeneric<IBrandsGeneric>;
  locale: string;
  md: string;
  slug: string;
  from?: string;
  heart?: boolean;
  handleSetIsResult?: (val: boolean) => void;
}

function ProductCard({
  data,
  md,
  locale,
  slug,
  from,
  heart = true,
  handleSetIsResult,
}: Props) {
  // cookies
  const { auth } = parseCookies();

  // api
  const {
    addFavorite,
    isLoadingAddFavorite,
    deleteFavorite,
    isLoadingDeleteFavorite,
    isSuccessAddFavorite,
  } = useFavorite();

  const discountAmount = (data.price * data.discount) / 100 || 0;
  const discountedPrice = data.price - discountAmount;

  const addFavorites = (e: UIEvent, val: number) => {
    e.stopPropagation();
    if (!isLoadingAddFavorite && auth) addFavorite(val);
  };

  const deleteFavorites = (e: UIEvent, val: number) => {
    e.stopPropagation();

    if (!isLoadingDeleteFavorite && auth) deleteFavorite(val);
  };

  return (
    <Link
      href={`${localePrefix(locale)}/${data.slug}`}
      className={md ? `col-md-${md} mb-3` : ""}
    >
      <Wrapper
        onClick={
          handleSetIsResult ? handleSetIsResult.bind(null, false) : () => {}
        }
      >
        <div className="product-card">
          <div className="img-container">
            <Image
              width={400}
              height={400}
              src={`${data.image}`}
              alt={data.title}
              loading="lazy"
              placeholder="blur"
              blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiNmOWY5ZjkiLz48c3RvcCBvZmZzZXQ9IjUwJSIgc3RvcC1jb2xvcj0iI2Y1ZjVmNSIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iI2YwZjBmMCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSJ1cmwoI2cpIi8+PC9zdmc+"
              quality={75}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
            {!!data.discount_type ? (
              <div className="product-discount-rec active">
                -{data.discount}
                {data.discount_type === "1" ? (
                  <svg
                    className="manatIconList"
                    width={33}
                    height={27}
                    viewBox="0 0 33 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                      fill="#FF4C4C"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                      fill="#FF4C4C"
                    />
                  </svg>
                ) : (
                  <span style={{ marginLeft: 5 }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <line x1="19" y1="5" x2="5" y2="19"></line>
                      <circle cx="6.5" cy="6.5" r="2.5"></circle>
                      <circle cx="17.5" cy="17.5" r="2.5"></circle>
                    </svg>
                  </span>
                )}
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className="content">
            <div className="product-card-detail">
              <div className="product-name-parent-card">
                <p className="product-name">{data.title}</p>
                {heart && auth && (
                  <div
                    className="product-heart-div"
                    data-favoritedd={isSuccessAddFavorite ? true : false}
                  >
                    {!data.fav ? (
                      <label
                        className={`product-heart`}
                        onClick={(e) => addFavorites.call(null, e, data.id)}
                      >
                        <input className="like-button" type="checkbox" />
                        <svg
                          width={14}
                          height={13}
                          viewBox="0 0 14 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            className="fil0 str0"
                            d="M12.0747 1.9548C11.7815 1.6521 11.4333 1.41198 11.0502 1.24815C10.667 1.08432 10.2563 1 9.84153 1C9.42676 1 9.01606 1.08432 8.63289 1.24815C8.24972 1.41198 7.90158 1.6521 7.60837 1.9548L6.99985 2.58271L6.39132 1.9548C5.79905 1.34365 4.99576 1.00032 4.15816 1.00032C3.32057 1.00032 2.51728 1.34365 1.92501 1.9548C1.33273 2.56594 1 3.39483 1 4.25912C1 5.12341 1.33273 5.9523 1.92501 6.56344L2.53353 7.19135L6.99985 11.8L11.4662 7.19135L12.0747 6.56344C12.368 6.26089 12.6007 5.90165 12.7595 5.50627C12.9183 5.11089 13 4.6871 13 4.25912C13 3.83114 12.9183 3.40735 12.7595 3.01197C12.6007 2.61659 12.368 2.25736 12.0747 1.9548V1.9548Z"
                            stroke="#fff"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </label>
                    ) : (
                      <label
                        className={`product-heart`}
                        onClick={(e) => deleteFavorites.call(null, e, data.id)}
                      >
                        <input className="like-button" type="checkbox" />
                        <svg
                          width={10}
                          height={10}
                          viewBox="0 0 10 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 1L1 9"
                            stroke="white"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M1 1L9 9"
                            stroke="white"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </label>
                    )}
                  </div>
                )}
              </div>
              <div className="product-bottom-card">
                {data.is_order === 0 ? (
                  <div
                    className={
                      data.discount && Number(data.discount)
                        ? "price priceDiscount"
                        : "price"
                    }
                  >
                    <p className="new-price">
                      {!!data.discount_type ? (
                        data.discount_type === "1" ? (
                          <>
                            {data.price - data.discount}
                            <svg
                              className="manatIcon"
                              width={33}
                              height={27}
                              viewBox="0 0 33 27"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                                fill="#FF4C4C"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                                fill="#FF4C4C"
                              />
                            </svg>
                          </>
                        ) : (
                          discountedPrice
                        )
                      ) : (
                        <>
                          {data.price}
                          <svg
                            className="manatIcon"
                            width={33}
                            height={27}
                            viewBox="0 0 33 27"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                              fill="#FF4C4C"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                              fill="#FF4C4C"
                            />
                          </svg>
                        </>
                      )}
                    </p>
                    {!!data.discount_type ? (
                      <p className="old-price newDiscount">
                        {data.price}{" "}
                        <svg
                          className="manatIcon"
                          width={33}
                          height={27}
                          viewBox="0 0 33 27"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                            fill="#FF4C4C"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                            fill="#FF4C4C"
                          />
                        </svg>
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                ) : (
                  <p className="product__only_order">{t("isOrder", locale)}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    </Link>
  );
}

export default ProductCard;

const Wrapper = styled.div`
  .product-card {
    display: block;
    height: 357px;
    box-shadow: 0 0 12px rgb(27 28 31 / 6%);
    border-radius: 6px;
    padding: 0.6rem 0.6rem 2.4rem;
    border: none;
  }

  .product-card .img-container {
    height: 233px;
    margin-bottom: 1.2rem;
  }

  .product-card,
  .product-card .img-container {
    background: #fff;
    position: relative;
  }

  .product-card .img-container img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0.6rem;
  }

  .product-card .content {
    height: auto;
    margin-top: 1.8rem;
    padding: 0 0.6rem;
    position: relative;
  }

  .product-card .content .product-card-detail .product-name {
    font-style: normal;
    font-weight: 300;
    font-size: 1.6rem;
    line-height: 125%;
    color: #1b1c1f;
    margin-bottom: 1.2rem;
    width: 200px;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    padding-right: 10px;
    overflow:hidden;
  }

  .product-heart-div {
    border: 2px solid rgba(255, 36, 89, 0.25);
    position: absolute;
    top: 0;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  .product-heart {
    background: linear-gradient(
      220.03deg,
      #fc184f 7.14%,
      rgba(253, 51, 100, 0.25) 89.91%
    );
    border-radius: 50%;
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 999;
  }

  .like-button {
    display: none;
  }

  .product-heart svg {
    width: auto;
    height: auto;
  }

  .product-card .content .product-bottom-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .product-card .content .product-card-detail .price {
    display: flex;
    align-items: flex-start;
  }

  .product-card-detail .price .new-price {
    display: inline-flex;
    align-items: center;
    font-style: normal;
    font-weight: 500;
    font-size: 2.4rem;
    line-height: 125%;
    letter-spacing: -0.05em;
    color: #1b1c1f;
  }

  .product-card .content .product-card-detail .price .new-price svg {
    width: 15px;
    margin-left: 5px;
  }

  .product-card .content {
    height: auto;
    margin-top: 1.8rem;
    padding: 0 0.6rem;
    position: relative;
  }
`;
